import React, { useState, useContext, useEffect } from 'react';
import {
	TextField,
	Button,
	Paper,
	Typography,
	MenuItem,
	InputLabel,
	FormControl,
	Select,
	Switch,
	FormControlLabel,
	Box,
	Stack
} from '@mui/material';
import SaveRoundedIcon from '@mui/icons-material/SaveRounded';

import { useNavigate } from 'react-router-dom';
import { DataContext } from '../../../context/DataContext';
import { getRuta, simpleMessage } from '../../../helpers/Helpers';
import { deleteToken, deleteUserData, getToken } from '../../../api/Account';
import { addRolAsync, getRolListAsync } from '../../../api/Rols';
import { MODULES, ALL_PERMISSIONS } from './rolModules';
import PermissionsPicker from './PermissionsPicker';
import BackButton from '../../../components/ui/BackButton';

const estadoInicial = () =>
	Object.fromEntries(ALL_PERMISSIONS.map(({ description }) => [description, false]));

const AddRol = () => {
	const token = getToken();
	let ruta = getRuta();
	let navigate = useNavigate();

	const { setIsLoading, setIsLogged, setIsUnautorized } = useContext(DataContext);

	const [rolName, setRolName] = useState('');
	const [isFullAccess, setIsFullAccess] = useState(false);
	const [rolesList, setRolesList] = useState([]);
	const [nivelJerarquico, setNivelJerarquico] = useState(0);

	const [permissions, setPermissions] = useState(estadoInicial);

	const handleChangeFullAccess = () => {
		setIsFullAccess((prev) => {
			const nuevoValor = !prev;
			setPermissions((previo) =>
				Object.fromEntries(Object.keys(previo).map((p) => [p, nuevoValor]))
			);
			return nuevoValor;
		});
	};

	const saveRol = async () => {
		if (!rolName.trim()) {
			simpleMessage('Ingrese un nombre para el nuevo rol', 'error');
			return;
		}

		const datos = {
			permissions: ALL_PERMISSIONS.map(({ description }) => ({
				description,
				IsEnable: permissions[description]
			})),
			roleName: rolName.trim(),
			isFullAccess,
			nivelJerarquico
		};

		setIsLoading(true);
		const result = await addRolAsync(token, datos);
		if (!result.statusResponse) {
			setIsLoading(false);
			if (result.error === 'eX01') {
				deleteUserData();
				deleteToken();
				setIsLogged(false);
				return;
			}

			if (result.error === 'Unauthorized') {
				setIsUnautorized(true);
				return;
			}

			simpleMessage(result.error, 'error');
			return;
		}
		setIsLoading(false);
		clearData();
		simpleMessage('Rol Agregado...!', 'success');
	};

	useEffect(() => {
		(async () => {
			setIsLoading(true);
			const result = await getRolListAsync(token);
			if (!result.statusResponse) {
				setIsLoading(false);
				if (result.error === 'Unauthorized') {
					setIsUnautorized(true);
					return;
				}
				simpleMessage(result.error, 'error');
				return;
			}

			setRolesList(result.data.result || []);
			setIsLoading(false);
		})();
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, []);

	const clearData = () => {
		setRolName('');
		setNivelJerarquico(0);
		setIsFullAccess(false);
		setPermissions(estadoInicial());
	};

	return (
		<div>
			<Paper sx={{ overflow: 'hidden' }}>
				<Stack
					direction="row"
					alignItems="center"
					spacing={2}
					sx={{ padding: '18px 22px', borderBottom: '1px solid', borderColor: 'divider' }}
				>
					<BackButton onClick={() => navigate(`${ruta}/rols`)} />
					<Box sx={{ minWidth: 0 }}>
						<Typography
							sx={{
								fontFamily: "'Poppins', sans-serif",
								fontWeight: 600,
								fontSize: 16.5,
								color: 'text.primary',
								lineHeight: 1.1
							}}
						>
							Agregar rol
						</Typography>
						<Typography sx={{ fontSize: 13, color: 'text.disabled', marginTop: '3px' }}>
							Configuración del nuevo rol y sus permisos
						</Typography>
					</Box>
				</Stack>

				<Box sx={{ padding: '22px' }}>
					<Stack
						spacing={2}
						direction={{ xs: 'column', sm: 'row' }}
						justifyContent="space-between"
						sx={{ marginBottom: '18px' }}
					>
						<TextField
							fullWidth
							size="small"
							onChange={(e) => setRolName(e.target.value.toUpperCase())}
							label={'Nombre del rol'}
							value={rolName}
						/>

						<FormControl fullWidth required size="small">
							<InputLabel id="rol-superior-label">Rol superior</InputLabel>
							<Select
								labelId="rol-superior-label"
								value={nivelJerarquico}
								onChange={(e) => setNivelJerarquico(e.target.value)}
								label="Rol superior"
								style={{ textAlign: 'left' }}
							>
								<MenuItem value={0}>
									<em>Seleccione un rol superior</em>
								</MenuItem>
								{rolesList.map((item) => (
									<MenuItem key={item.id} value={item.id}>
										{item.rolName}
									</MenuItem>
								))}
							</Select>
						</FormControl>
					</Stack>

					<Stack
						direction="row"
						alignItems="center"
						spacing={1}
						sx={{ marginBottom: '18px' }}
					>
						<Typography sx={{ fontSize: 13.5, fontWeight: 700, color: 'text.secondary' }}>
							Acceso total al sistema
						</Typography>
						<FormControlLabel
							control={<Switch checked={isFullAccess} onChange={handleChangeFullAccess} />}
							label={isFullAccess ? 'Activado' : 'Desactivado'}
						/>
					</Stack>

					<PermissionsPicker
						modules={MODULES}
						permissions={permissions}
						onToggle={(desc) => setPermissions((prev) => ({ ...prev, [desc]: !prev[desc] }))}
						onToggleMany={(descs, valor) =>
							setPermissions((prev) => {
								const next = { ...prev };
								descs.forEach((d) => {
									next[d] = valor;
								});
								return next;
							})
						}
					/>

					<Button
						variant="contained"
						color="primary"
						sx={{ marginTop: '20px' }}
						startIcon={<SaveRoundedIcon />}
						onClick={saveRol}
					>
						Guardar rol
					</Button>
				</Box>
			</Paper>
		</div>
	);
};

export default AddRol;
